<template>
  <div class="container">
    <!-- 顶部标题+筛选区 -->
    <div class="container01">
      <h1 class="container01_h1">订单管理</h1>

      <!-- 移动端简化筛选 -->
      <el-collapse v-if="isMobile" class="mobile-filter">
        <el-collapse-item title="筛选条件">
          <el-form :model="filterForm" label-width="80px">
            <el-form-item label="客户">
              <el-select v-model="filterForm.customer_id" clearable placeholder="选择客户" filterable style="width: 100%">
                <el-option v-for="customer in customers" :key="customer.customer_id" :label="customer.name"
                  :value="customer.customer_id">
                </el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="状态">
              <el-select v-model="filterForm.status" clearable placeholder="选择状态" style="width: 100%">
                <el-option label="正常" value="正常"></el-option>
                <el-option label="已完成" value="已完成"></el-option>
                <el-option label="已取消" value="已取消"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="开始日期">
              <el-date-picker v-model="filterForm.start_date" type="date" placeholder="选择开始日期" value-format="YYYY-MM-DD"
                style="width: 100%">
              </el-date-picker>
            </el-form-item>
            <el-form-item label="结束日期">
              <el-date-picker v-model="filterForm.end_date" type="date" placeholder="选择结束日期" value-format="YYYY-MM-DD"
                style="width: 100%">
              </el-date-picker>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="handleFilter" style="width: 100%">筛选</el-button>
              <el-button @click="resetFilter" style="width: 100%; margin-top: 10px;">重置</el-button>
            </el-form-item>
          </el-form>
        </el-collapse-item>
      </el-collapse>

      <!-- 桌面端筛选条件 -->
      <el-card v-else header="筛选条件" style="margin-bottom: 20px;">
        <el-form :model="filterForm" label-width="100px">
          <el-row :gutter="20">
            <el-col :span="6">
              <el-form-item label="客户">
                <el-select v-model="filterForm.customer_id" clearable placeholder="选择客户" filterable>
                  <el-option v-for="customer in customers" :key="customer.customer_id" :label="customer.name"
                    :value="customer.customer_id">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="6">
              <el-form-item label="状态">
                <el-select v-model="filterForm.status" clearable placeholder="选择状态">
                  <el-option label="正常" value="正常"></el-option>
                  <el-option label="已完成" value="已完成"></el-option>
                  <el-option label="已取消" value="已取消"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="6">
              <el-form-item label="开始日期">
                <el-date-picker v-model="filterForm.start_date" type="date" placeholder="选择开始日期"
                  value-format="YYYY-MM-DD">
                </el-date-picker>
              </el-form-item>
            </el-col>
            <el-col :span="6">
              <el-form-item label="结束日期">
                <el-date-picker v-model="filterForm.end_date" type="date" placeholder="选择结束日期"
                  value-format="YYYY-MM-DD">
                </el-date-picker>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item>
            <el-button type="primary" @click="handleFilter">筛选</el-button>
            <el-button @click="resetFilter">重置</el-button>
          </el-form-item>
        </el-form>
      </el-card>

      <!-- 操作按钮 - 移动端改为垂直 -->
      <div :class="['button-container', { 'mobile-buttons': isMobile }]">
        <el-button @click="$router.back()" class="mobile-btn">返回上一级</el-button>
        <el-button type="primary" @click="$router.push('/orders/create')" class="mobile-btn">新建订单</el-button>
        <el-button type="success" @click="handleBatchPrint" :disabled="selectedOrderIds.length === 0"
          :loading="batchPrinting" class="mobile-btn">
          批量打印 ({{ selectedOrderIds.length }})
        </el-button>
        <el-button type="danger" @click="handleBatchDelete" :disabled="selectedOrderIds.length === 0"
          :loading="batchDeleting" class="mobile-btn">
          批量删除 ({{ selectedOrderIds.length }})
        </el-button>
      </div>

      <!-- 搜索栏 - 移动端优化 -->
      <div class="search-container" :class="{ 'mobile-search': isMobile }">
        <el-input v-model="searchKeyword" placeholder="请输入订单编号或客户名称" clearable @keyup.enter="handleSearch"
          :class="{ 'mobile-input': isMobile }">
        </el-input>
        <el-button type="primary" @click="handleSearch" :class="{ 'mobile-search-btn': isMobile }">搜索</el-button>
      </div>
    </div>

    <!-- 订单表格+分页 -->
    <div class="container02">
      <div class="table-wrapper">
        <el-table :data="paginatedData" border v-loading="loading" @selection-change="handleSelectionChange"
          :class="{ 'mobile-table': isMobile }">
          <el-table-column v-if="!isMobile" type="selection" width="55"></el-table-column>
          <el-table-column prop="order_number" label="订单号" :width="isMobile ? 120 : 150"></el-table-column>
          <el-table-column v-if="!isMobile" prop="customer_name" label="客户名称" min-width="150"></el-table-column>
          <el-table-column v-if="!isMobile" prop="order_time" label="开单时间" width="180">
            <template #default="scope">
              {{ formatDateTime(scope.row.order_time) }}
            </template>
          </el-table-column>
          <el-table-column prop="total_amount" label="金额" width="120">
            <template #default="scope">
              ¥{{ scope.row.total_amount?.toFixed(2) || '0.00' }}
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="100">
            <template #default="scope">
              <el-tag :type="getStatusType(scope.row.status)" :size="isMobile ? 'small' : 'default'">
                {{ scope.row.status }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" :width="isMobile ? 120 : 250">
            <template #default="scope">
              <el-dropdown v-if="isMobile" trigger="click" @command="handleCommand">
                <span class="el-dropdown-link">
                  操作
                </span>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item :command="{ action: 'view', id: scope.row.order_id }">详情</el-dropdown-item>
                    <el-dropdown-item :command="{ action: 'edit', id: scope.row.order_id }">编辑</el-dropdown-item>
                    <el-dropdown-item :command="{ action: 'print', id: scope.row.order_id }">打印</el-dropdown-item>
                    <el-dropdown-item :command="{ action: 'delete', id: scope.row.order_id }">删除</el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
              <div v-else>
                <el-button type="text" @click="handleViewDetail(scope.row.order_id)">详情</el-button>
                <el-button type="text" @click="handleEdit(scope.row.order_id)">编辑</el-button>
                <el-button type="text" @click="handlePrint(scope.row.order_id)">打印</el-button>
                <el-button type="text" @click="handleDelete(scope.row.order_id)" style="color: #f56c6c;">删除</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页 - 移动端优化 -->
      <div class="pagination" :class="{ 'mobile-pagination': isMobile }">
        <button @click="prevPage" :disabled="currentPage <= 1" class="page-button">上一页</button>
        <span class="page-info">第 {{ currentPage }} 页/共 {{ totalPages }} 页</span>
        <button @click="nextPage" :disabled="currentPage >= totalPages" class="page-button">下一页</button>
      </div>
    </div>

    <!-- 打印容器（隐藏，仅打印时显示） -->
    <div ref="printContainer" class="print-container"></div>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox, ElCollapse, ElCollapseItem, ElForm, ElFormItem, ElSelect, ElOption, ElDatePicker, ElButton, ElCard, ElRow, ElCol, ElInput, ElTable, ElTableColumn, ElTag, ElDropdown, ElDropdownMenu, ElDropdownItem } from 'element-plus'
import ordersAPI from '@/api/orders'
import customersAPI from '@/api/customers'

export default {
  name: 'OrdersListView',
  components: {
    ElCollapse,
    ElCollapseItem,
    ElForm,
    ElFormItem,
    ElSelect,
    ElOption,
    ElDatePicker,
    ElButton,
    ElCard,
    ElRow,
    ElCol,
    ElInput,
    ElTable,
    ElTableColumn,
    ElTag,
    ElDropdown,
    ElDropdownMenu,
    ElDropdownItem
  },
  data() {
    return {
      orders: [],
      searchKeyword: '',
      loading: false,
      batchDeleting: false,
      batchPrinting: false,
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      selectedOrderIds: [],
      filterForm: {
        customer_id: null,
        status: '',
        start_date: '',
        end_date: ''
      },
      customers: [],
      useFilter: false,
      isMobile: false
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalCount / this.pageSize) || 1
    },
    paginatedData() {
      return this.orders
    }
  },
  created() {
    this.fetchCustomers()
    this.fetchOrdersData()
    this.checkScreenSize()
    window.addEventListener('resize', this.checkScreenSize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScreenSize)
  },
  methods: {
    checkScreenSize() {
      this.isMobile = window.innerWidth < 768
    },
    handleCommand(command) {
      switch (command.action) {
        case 'view':
          this.handleViewDetail(command.id)
          break
        case 'edit':
          this.handleEdit(command.id)
          break
        case 'print':
          this.handlePrint(command.id)
          break
        case 'delete':
          this.handleDelete(command.id)
          break
      }
    },
    async fetchCustomers() {
      try {
        const response = await customersAPI.inquireCustomers({ per_page: 1000 })
        this.customers = response.customers || []
      } catch (error) {
        console.error('获取客户列表失败:', error)
        ElMessage.error('获取客户列表失败')
      }
    },
    async fetchOrdersData() {
      this.loading = true
      try {
        let response
        if (this.useFilter) {
          const params = {
            ...this.filterForm,
            page: this.currentPage,
            per_page: this.pageSize
          }
          Object.keys(params).forEach(key => {
            if (params[key] === '' || params[key] === null || params[key] === undefined) {
              delete params[key]
            }
          })
          response = await ordersAPI.filterOrders(params)
        } else {
          const params = {
            keyword: this.searchKeyword,
            page: this.currentPage,
            per_page: this.pageSize
          }
          response = await ordersAPI.inquireOrders(params)
        }
        this.orders = response?.orders || []
        this.totalCount = response?.total || 0
        this.currentPage = response?.page || 1
      } catch (error) {
        console.error('获取订单列表失败:', error)
        ElMessage.error('获取订单列表失败')
        this.orders = []
        this.totalCount = 0
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.useFilter = false
      this.currentPage = 1
      this.fetchOrdersData()
    },
    handleFilter() {
      this.useFilter = true
      this.currentPage = 1
      this.fetchOrdersData()
    },
    resetFilter() {
      this.filterForm = {
        customer_id: null,
        status: '',
        start_date: '',
        end_date: ''
      }
      this.useFilter = false
      this.currentPage = 1
      this.fetchOrdersData()
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.fetchOrdersData()
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.fetchOrdersData()
      }
    },
    handleViewDetail(orderId) {
      this.$router.push(`/orders/detail/${orderId}`)
    },
    handleEdit(orderId) {
      this.$router.push({
        path: '/orders/create',
        query: { edit: true, order_id: orderId }
      })
    },
    async handlePrint(orderId) {
      try {
        this.loading = true;
        const response = await ordersAPI.inquireOrder(orderId);
        const order = response;
        if (!order || !order.order_id) {
          ElMessage.error('订单数据异常，无法打印');
          this.loading = false;
          return;
        }
        const printHtml = this.buildOrderPrintHtml([order]);
        const printContainer = this.$refs.printContainer;
        printContainer.innerHTML = printHtml;
        printContainer.style.display = 'block';
        setTimeout(() => {
          window.print();
          printContainer.style.display = 'none';
          printContainer.innerHTML = '';
          this.loading = false;
        }, 500);
      } catch (error) {
        console.error('打印失败:', error);
        ElMessage.error('打印失败，请检查接口或网络');
        this.loading = false;
      }
    },
    async handleBatchPrint() {
      if (this.selectedOrderIds.length === 0) {
        ElMessage.warning('请先选择要打印的订单');
        return;
      }
      try {
        await ElMessageBox.confirm(
          `确认批量打印 ${this.selectedOrderIds.length} 个订单？\n确认后只需点击一次打印按钮！`,
          '批量打印确认',
          { confirmButtonText: '确认打印', cancelButtonText: '取消', type: 'info' }
        );
      } catch (e) {
        return;
      }
      try {
        this.batchPrinting = true;
        ElMessage.info('正在加载订单数据，请稍候...');
        const orderPromises = this.selectedOrderIds.map(id => ordersAPI.inquireOrder(id));
        const responses = await Promise.allSettled(orderPromises);
        const successOrders = [];
        const failIds = [];
        responses.forEach((res, index) => {
          if (res.status === 'fulfilled') {
            const order = res.value;
            if (order && order.order_id) {
              successOrders.push(order);
            } else {
              failIds.push(this.selectedOrderIds[index]);
            }
          } else {
            failIds.push(this.selectedOrderIds[index]);
          }
        });
        if (successOrders.length === 0) {
          ElMessage.error('所有选中订单均无法加载，打印失败');
          this.batchPrinting = false;
          return;
        }
        const printHtml = this.buildOrderPrintHtml(successOrders);
        const printContainer = this.$refs.printContainer;
        printContainer.innerHTML = printHtml;
        printContainer.style.display = 'block';
        setTimeout(() => {
          window.print();
          printContainer.style.display = 'none';
          printContainer.innerHTML = '';
          this.batchPrinting = false;
          const successMsg = `成功加载 ${successOrders.length} 个订单`;
          const failMsg = failIds.length > 0 ? `，${failIds.length} 个订单加载失败` : '';
          ElMessage.success(successMsg + failMsg + '，请在打印对话框中确认打印');
        }, 800);
      } catch (error) {
        console.error('批量打印失败:', error);
        ElMessage.error('批量打印失败');
        this.batchPrinting = false;
      }
    },
    buildOrderPrintHtml(orders) {
      let printContent = '';
      orders.forEach((order, index) => {
        const orderHtml = `
          <div style="font-family: 'SimSun', serif; margin: 20px; font-size: 14px;">
            <div class="print-header" style="text-align: center; margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 10px;">
              <h2 style="margin: 0; font-size: 24px;">销售订单</h2>
              <p style="margin: 5px 0 0 0;">订单编号: ${order.order_number || '-'}</p>
            </div>
            <div class="print-info" style="margin: 20px 0;">
              <p style="margin: 5px 0; display: inline-block; margin-right: 30px;">客户: ${order.customer_name || '未知客户'}</p>
              <p style="margin: 5px 0; display: inline-block; margin-right: 30px;">开单时间: ${this.formatDateTime(order.order_time) || '-'}</p>
              <p style="margin: 5px 0; display: inline-block; margin-right: 30px;">配送时间: ${this.formatDateTime(order.delivery_time) || '-'}</p>
              <p style="margin: 5px 0; display: inline-block;">订单状态: ${order.status || '-'}</p>
            </div>
            <table class="print-table" style="width: 100%; border-collapse: collapse; font-size: 14px;" border="1">
              <thead>
                <tr>
                  <th style="padding: 8px; text-align: center; border: 1px solid #000; background-color: #f5f5f5;">序号</th>
                  <th style="padding: 8px; text-align: center; border: 1px solid #000; background-color: #f5f5f5;">商品名称</th>
                  <th style="padding: 8px; text-align: center; border: 1px solid #000; background-color: #f5f5f5;">规格</th>
                  <th style="padding: 8px; text-align: center; border: 1px solid #000; background-color: #f5f5f5;">数量</th>
                  <th style="padding: 8px; text-align: center; border: 1px solid #000; background-color: #f5f5f5;">单价</th>
                  <th style="padding: 8px; text-align: center; border: 1px solid #000; background-color: #f5f5f5;">金额</th>
                </tr>
              </thead>
              <tbody>
                ${order.items && order.items.length > 0
                ? order.items.map((item, idx) => `
                  <tr>
                    <td style="padding: 8px; text-align: center; border: 1px solid #000;">${idx + 1}</td>
                    <td style="padding: 8px; text-align: center; border: 1px solid #000;">${item.merchandise_name || '-'}</td>
                    <td style="padding: 8px; text-align: center; border: 1px solid #000;">${item.specification || '-'}</td>
                    <td style="padding: 8px; text-align: right; border: 1px solid #000;">${item.quantity || 0}</td>
                    <td style="padding: 8px; text-align: right; border: 1px solid #000;">¥${item.price?.toFixed(2) || '0.00'}</td>
                    <td style="padding: 8px; text-align: right; border: 1px solid #000;">¥${item.amount?.toFixed(2) || '0.00'}</td>
                  </tr>
                `).join('')
                : `<tr><td style="padding: 8px; text-align: center; border: 1px solid #000;" colspan="6">暂无商品明细</td></tr>`
              }
              </tbody>
              <tfoot>
                <tr>
                  <td style="padding: 8px; text-align: right; border: 1px solid #000; font-weight: bold;" colspan="5">合计金额:</td>
                  <td style="padding: 8px; text-align: right; border: 1px solid #000; font-weight: bold;">¥${order.total_amount?.toFixed(2) || '0.00'}</td>
                </tr>
              </tfoot>
            </table>
            <div class="print-footer" style="margin-top: 30px;">
              <p style="margin: 10px 0;">制单人: ___________</p>
              <p style="margin: 10px 0;">客户签字: ___________</p>
            </div>
          </div>
        `;
        printContent += orderHtml;
        if (index < orders.length - 1) {
          printContent += '<div style="page-break-after: always; height: 0;"></div>';
        }
      });
      return `
        <div style="padding: 0;">
          <style>
            @media print {
              body { margin: 0; font-family: 'SimSun', serif; }
              .page-break { page-break-after: always !important; }
            }
          </style>
          ${printContent}
        </div>
      `;
    },
    async handleDelete(orderId) {
      try {
        await ElMessageBox.confirm('确定要删除该订单吗？删除后数据不可恢复！', '删除确认', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        await ordersAPI.deleteOrder(orderId)
        ElMessage.success('订单删除成功')
        this.fetchOrdersData()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除订单失败:', error)
          ElMessage.error('删除订单失败')
        }
      }
    },
    async handleBatchDelete() {
      if (this.selectedOrderIds.length === 0) {
        ElMessage.warning('请先选择要删除的订单')
        return
      }
      try {
        await ElMessageBox.confirm(
          `确定要删除选中的 ${this.selectedOrderIds.length} 个订单吗？删除后数据不可恢复！`,
          '批量删除确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        this.batchDeleting = true
        const deletePromises = this.selectedOrderIds.map(orderId => ordersAPI.deleteOrder(orderId))
        await Promise.all(deletePromises)
        ElMessage.success(`成功删除 ${this.selectedOrderIds.length} 个订单`)
        this.selectedOrderIds = []
        this.fetchOrdersData()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('批量删除失败:', error)
          ElMessage.error('批量删除失败')
        }
      } finally {
        this.batchDeleting = false
      }
    },
    handleSelectionChange(selectedRows) {
      this.selectedOrderIds = selectedRows.map(row => row.order_id)
    },
    formatDateTime(dateTime) {
      if (!dateTime) return ''
      const date = new Date(dateTime)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(/\//g, '-')
    },
    getStatusType(status) {
      const statusMap = {
        '正常': 'primary',
        '已完成': 'success',
        '已取消': 'danger'
      }
      return statusMap[status] || 'info'
    }
  }
}
</script>

<style scoped>
.container {
  padding: 20px;
  animation: fadeIn 0.3s ease;
}
.container01_h1 {
  text-align: center;
  margin-bottom: 24px;
  font-size: 28px;
  font-weight: 600;
  background: linear-gradient(135deg, #1e293b, #3b82f6);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.button-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
  justify-content: center;
}
.search-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}
.search-container .el-input {
  flex: 1;
  min-width: 200px;
}
.table-wrapper {
  overflow-x: auto;
  border-radius: 16px;
  background: white;
  box-shadow: var(--card-shadow);
}
.el-table {
  border-radius: 16px;
  overflow: hidden;
}
.el-table__row:hover > td {
  background-color: #fef9e3 !important;
}
.el-tag {
  transition: all 0.2s;
}
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
}
.page-button {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 40px;
  padding: 6px 20px;
  transition: all 0.2s;
  cursor: pointer;
}
.page-button:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #cbd5e1;
  transform: translateY(-1px);
}
.print-container {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 99999;
  background: #fff;
  padding: 20px;
  width: 100%;
  height: 100%;
  overflow: auto;
  display: none;
  box-sizing: border-box;
}
@media (max-width: 768px) {
  .container {
    padding: 12px !important;
  }
  .button-container {
    flex-direction: column;
    gap: 8px;
  }
  .search-container {
    flex-direction: column;
  }
  .mobile-filter .el-collapse-item__header {
    font-weight: 600;
    background: #f8fafc;
    border-radius: 12px;
    padding: 8px 16px;
  }
  .el-button {
    width: 100%;
    margin: 0 !important;
  }
  .table-wrapper {
    margin-top: 12px;
  }
  .el-table {
    font-size: 12px;
  }
  .pagination {
    flex-direction: column;
    gap: 12px;
  }
  .page-button {
    width: 90%;
    margin: 0 auto;
  }
  .el-dropdown-link {
    background: var(--primary-color);
    color: white;
    padding: 6px 12px;
    border-radius: 30px;
    display: inline-block;
    text-align: center;
    width: 80px;
  }
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
<style>
@media print {
  body * {
    visibility: hidden !important;
  }
  .print-container,
  .print-container * {
    visibility: visible !important;
  }
  .print-container {
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    display: block !important;
    width: 100% !important;
    height: auto !important;
    padding: 20px !important;
    background: #fff !important;
    border: none !important;
    box-shadow: none !important;
  }
  .page-break {
    page-break-after: always !important;
  }
  @page {
    margin: 1cm;
    size: A4;
  }
}
</style>